/**
 * errorMaps/inventory.ts — mapeo canónico de errores de Inventario (P13-V2B, Property 3).
 *
 * Renderiza el vocabulario de error V2A completo. Tres convenciones de cuerpo conviven en el
 * runtime y TODAS se leen aquí para que ningún código se pierda:
 *   · { code, message }            — convención principal de los controladores canónicos
 *   · { error }                    — namespace field-supervisor
 *   · Error locales con .code/.status directamente en la instancia (refusals 410 locales)
 * El error resultante SIEMPRE porta code/status/retryable, de modo que la UI pueda distinguir un
 * conflicto REINTENTABLE (replay M-6 seguro) de uno TERMINAL.
 */

export interface InventoryUiError extends Error {
  code?: string
  status?: number
  retryable?: boolean
}

const CODE_MESSAGES: Record<string, string> = {
  // ── Vocabulario V2A (M-6 idempotencia · FSM · alcance · retiros) ──
  IDEMPOTENCY_KEY_REQUIRED: 'La operación requiere clave de idempotencia. Reintenta la acción; si persiste, recarga la página.',
  IDEMPOTENCY_PAYLOAD_CONFLICT: 'Esta clave de operación ya se usó con datos distintos. Revisa los datos y ejecútala como una operación nueva.',
  IDEMPOTENCY_OPERATION_IN_PROGRESS: 'La operación sigue en proceso. Reintenta en unos segundos: se reanudará sin duplicar el efecto.',
  REQUEST_CREATION_SCOPE_DENIED: 'Tu autoridad actual no permite crear solicitudes en ese contexto.',
  REQUEST_NOT_FOUND: 'Solicitud de inventario no encontrada.',
  ORDER_NOT_FOUND: 'La orden no existe o no es visible desde tu alcance.',
  INVALID_REQUEST_TRANSITION: 'La solicitud no admite esa transición de estado.',
  INVALID_OUTBOUND_TRANSITION: 'La orden de salida no admite esa transición de estado.',
  REQUEST_DELIVERY_OWNED_BY_OUTBOUND_ORDER: 'La entrega de esta solicitud pertenece a su orden de salida: gestiónala desde la orden.',
  REQUEST_ALREADY_HAS_OUTBOUND_ORDER: 'La solicitud ya tiene una orden de salida activa.',
  LEGACY_APPROVED_REQUEST_SEMANTICS_UNSAFE: 'Solicitud aprobada antes del corte sin procedencia canónica; requiere adopción por el administrador antes de continuar.',
  WORKSPACE_DESTINATION_RETIRED: 'El destino workspace fue retirado del producto.',
  INVENTORY_ATTACHMENT_UPLOADS_RETIRED: 'La carga de adjuntos de inventario fue retirada.',
  LEGACY_SURFACE_RETIRED: 'Esta superficie de inventario fue retirada.',
  OUTBOUND_ADMINISTRATION_UNDERIVABLE: 'No se pudo derivar la administración de la orden de salida.',
  INVALID_QUANTITY: 'Las cantidades de inventario deben ser enteros positivos.',
  INSUFFICIENT_STOCK: 'Stock insuficiente para completar la operación.',
  MUTATION_RETRY_REQUIRED: 'Conflicto transitorio de concurrencia. Reintenta la operación: es seguro.',
  AGENCY_ASSIGNMENT_SCOPE_DENIED: 'Tu asignación de agencia no permite esta acción.',

  // ── Códigos pre-V2A que siguen vivos en rutas antiguas ──
  OVER_RESERVED_STATE: 'Estado de reservas inconsistente. Contacta al administrador.',
  INVALID_STATUS_TRANSITION: 'Transición de estado inválida para la entidad.',
  VALIDATION_ERROR: 'Datos inválidos o incompletos en la solicitud.',
  ITEM_NOT_FOUND: 'Artículo no encontrado en el inventario.',
  TRANSFER_CONFLICT: 'Conflicto al procesar la transferencia.',
  DUPLICATE_REQUEST: 'Solicitud duplicada detectada.',
  PERMISSION_DENIED: 'Permisos insuficientes para esta acción.',
  RATE_LIMITED: 'Demasiadas operaciones seguidas. Intenta nuevamente en unos segundos.',
  LOCK_CONFLICT: 'Recurso bloqueado temporalmente. Reintenta.',
  IDEMPOTENCY_KEY_CONFLICT: 'Esta clave de operación ya se usó con datos distintos. Revisa los datos y ejecútala como una operación nueva.'
}

/** Códigos cuyo reintento con la MISMA clave es un replay M-6 seguro. */
const RETRYABLE_CODES = new Set([
  'IDEMPOTENCY_OPERATION_IN_PROGRESS',
  'MUTATION_RETRY_REQUIRED',
  'LOCK_CONFLICT',
  'RATE_LIMITED'
])

export function mapInventoryError(code: string | undefined, backendMessage?: string): string {
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code]
  if (backendMessage) return backendMessage
  return 'Error de inventario no especificado.'
}

export function extractInventoryError(err: any): InventoryUiError {
  const body = err?.response?.data
  const status: number | undefined = err?.response?.status ?? err?.status
  const code: string | undefined = body?.code ?? err?.code
  const backendMsg: string | undefined =
    (typeof body?.message === 'string' && body.message) ||
    (typeof body?.error === 'string' && body.error) ||
    (!err?.response && typeof err?.message === 'string' && err.message) ||
    undefined

  let mapped = code && CODE_MESSAGES[code] ? CODE_MESSAGES[code] : undefined
  // Un 410 sin código sigue siendo un retiro: no debe degradar a "error no especificado".
  if (!mapped && status === 410) mapped = CODE_MESSAGES.LEGACY_SURFACE_RETIRED

  const error = new Error(mapped || backendMsg || 'Error de inventario no especificado.') as InventoryUiError
  error.code = code ?? (status === 410 ? 'LEGACY_SURFACE_RETIRED' : undefined)
  error.status = status
  // Hallazgo codex (P2): la clasificacion de reintento de la UI debe coincidir con el ciclo de
  // idempotencia (isRetryableIdempotencyError) — un error de red (sin status) o cualquier 5xx
  // conserva su Idempotency-Key y ES seguro de reintentar, con o sin flag en el body.
  error.retryable = body?.retryable === true || (code ? RETRYABLE_CODES.has(code) : false) ||
    status == null || (typeof status === 'number' && status >= 500)
  return error
}
